import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { LogIn, ArrowRight, ShieldCheck, Menu, X } from "lucide-react";
import { publicAsset } from "../../../features/tenantPath";
import { TENANT_RESOLVE_URL } from "../../../features/UrlHelper";
import { navrhSlugu } from "./CompanyRequestForm";
import VzhladTlacidlo from "../../../components/VzhladPrepinac/VzhladTlacidlo";
import "./LandingHeader.scss";

/**
 * Hlavicka marketingovej stranky. "Prihlasit sa" otvori panel s dvoma cestami:
 *   - do prevadzky: zakaznik/zamestnanec zada webovu adresu (slug), overime ju
 *     cez /api/tenant/resolve a presmerujeme na rozvozy.sk/<slug>?prihlasenie
 *     (Header prevadzky podla toho rovno otvori prihlasovanie),
 *   - do spravy systemu (/sprava) pre dodavatela.
 * Presmerovanie je cez window.location -- basename routera sa urcuje pri nacitani.
 */
const ODKAZY = [
  { href: "#funkcie", label: "Funkcie" },
  { href: "#ako-to-funguje", label: "Ako to funguje" },
  { href: "#pre-koho", label: "Pre koho" },
  { href: "#otazky", label: "Otázky" },
  { href: "#kontakt", label: "Kontakt" },
];

const bezPohybu = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function LandingHeader({ onCta }) {
  // Kotvy (#funkcie, …) sa posúvajú v JS, nie natívne: `scroll-behavior`
  // na .landing nemal účinok (posúva sa celý dokument, nie ten div), takže
  // stránka len skočila.
  const [panel, setPanel] = useState(false);
  const [menu, setMenu] = useState(false);
  const [adresa, setAdresa] = useState("");
  const [chyba, setChyba] = useState("");
  const [overujem, setOverujem] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!panel) return undefined;
    const zavri = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setPanel(false);
    };
    const esc = (e) => e.key === "Escape" && setPanel(false);
    document.addEventListener("mousedown", zavri);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", zavri);
      document.removeEventListener("keydown", esc);
    };
  }, [panel]);

  const pokracuj = async (e) => {
    e.preventDefault();
    // aj ked niekto vlozi celu adresu "rozvozy.sk/veteran"
    const slug = navrhSlugu(adresa.trim().replace(/^https?:\/\//i, "").replace(/^(www\.)?rozvozy\.sk\/?/i, ""));
    if (!slug) {
      setChyba("Zadajte webovú adresu vašej prevádzky.");
      return;
    }
    setChyba("");
    setOverujem(true);
    try {
      await axios.get(TENANT_RESOLVE_URL, { headers: { "X-Company-Slug": slug } });
      window.location.assign(`/${slug}/?prihlasenie`);
    } catch (err) {
      const kod = err?.response?.status;
      setChyba(
        kod === 404 || kod === 400
          ? `Prevádzku s adresou rozvozy.sk/${slug} nepoznáme. Skontrolujte ju, prosím.`
          : "Adresu sa nepodarilo overiť. Skúste to znova."
      );
      setOverujem(false);
    }
  };

  const prejdiNa = (e) => {
    const href = e.currentTarget.getAttribute("href");
    const ciel = document.getElementById(href.slice(1));
    setMenu(false);
    if (!ciel) return;
    e.preventDefault();
    // scroll-margin-top v LandingPage.scss drží sekciu pod sticky hlavičkou.
    ciel.scrollIntoView({ behavior: bezPohybu() ? "auto" : "smooth", block: "start" });
    // Adresa s kotvou ako pri natívnom odkaze (dá sa poslať, Späť funguje).
    window.history.pushState(null, "", href);
  };

  const klikCta = () => {
    setMenu(false);
    onCta();
  };

  return (
    <header className="landing-header">
      <div className="landing-header__inner">
        <a href="#top" className="landing-header__logo" aria-label="Rozvozy – na začiatok" onClick={prejdiNa}>
          <img src={publicAsset("rozvozy_icon.png")} height="35" alt="Rozvozy" />
        </a>

        <nav className={`landing-header__nav${menu ? " landing-header__nav--open" : ""}`} aria-label="Hlavná navigácia">
          {ODKAZY.map(({ href, label }) => (
            <a key={href} href={href} className="landing-header__link" onClick={prejdiNa}>
              {label}
            </a>
          ))}
          <button type="button" className="landing-header__cta" onClick={klikCta}>
            Vyskúšať demo
          </button>
        </nav>

        <div className="landing-header__actions" ref={panelRef}>
          <button
            type="button"
            className="landing-header__login"
            aria-expanded={panel}
            aria-haspopup="dialog"
            onClick={() => setPanel((p) => !p)}
          >
            <LogIn size={16} aria-hidden="true" /> Prihlásiť sa
          </button>

          {panel && (
            <div className="landing-login" role="dialog" aria-label="Prihlásenie">
              <form onSubmit={pokracuj}>
                <h3 className="landing-login__title">Prihlásenie do prevádzky</h3>
                <p className="landing-login__text">
                  Zadajte webovú adresu svojej prevádzky — tú, ktorú ste dostali v e-maile s prístupom.
                </p>
                <label className="landing-login__field">
                  <span className="landing-login__prefix">rozvozy.sk/</span>
                  <input
                    autoFocus
                    value={adresa}
                    onChange={(e) => setAdresa(e.target.value)}
                    placeholder="vasa-prevadzka"
                    aria-label="Webová adresa prevádzky"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                </label>
                {chyba && <p className="landing-login__error" role="alert">{chyba}</p>}
                <button type="submit" className="landing__btn landing__btn--primary landing-login__submit" disabled={overujem}>
                  {overujem ? "Overujem..." : <>Pokračovať <ArrowRight size={16} aria-hidden="true" /></>}
                </button>
              </form>
              <div className="landing-login__divider" />
              <a href="/sprava" className="landing-login__platform">
                <ShieldCheck size={16} aria-hidden="true" /> Správa systému
              </a>
            </div>
          )}

          <VzhladTlacidlo />

          <button
            type="button"
            className="landing-header__burger"
            aria-label={menu ? "Zavrieť menu" : "Otvoriť menu"}
            aria-expanded={menu}
            onClick={() => setMenu((m) => !m)}
          >
            {menu ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
    </header>
  );
}

export default LandingHeader;
